import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import PageHeader from '../../components/PageHeader';
import PageHeadline from '../../components/PageHeadline';
import CustomButton from '../../components/CustomButton';
import { makeOrganizationDetailUrl, paths } from '../../routes';
import { tenantSelector } from '../../selector/authenticationSelector';
import {
    useCreateOrganizationMutation,
    useCreateOrganizationUnitMutation,
    useGetCustomerClassesQuery,
} from '../../api/organization/organizationApiSlice';
import { isValidBillingEmail, isValidCountryCode, isValidVatId } from '../../utils/organizationValidation';
import { leitwegIdProblem } from '../../utils/leitwegId';
import { useViewedTenantAccount } from '../../hooks/useViewedTenantAccount';
import { shouldShowBillingSection } from './organizationSectionVisibility';
import OrgBasicInfoSection, { OrgBasicInfo } from './components/OrgBasicInfoSection';
import OrgBillingSection, { OrgBilling } from './components/OrgBillingSection';
import OrgClassSection from './components/OrgClassSection';
import OrgUnitsSection, { OrgUnitFormRow } from './components/OrgUnitsSection';

interface CreateOrgFormState {
    basic: OrgBasicInfo;
    customerClassId: string | null;
    billing: OrgBilling;
    units: OrgUnitFormRow[];
}

const makeInitialBasic = (): OrgBasicInfo => ({
    legalName: '',
    displayName: '',
    // Deliberately unset — see `OrgBasicInfo.type`. This used to be `'club'`.
    type: '',
    // #2102 item 1. `active` is also the column default, so a create that never
    // touches the control and one that chooses "active" store the same thing.
    status: 'active',
    contactEmail: '',
    notes: '',
});

const makeInitialBilling = (): OrgBilling => ({
    street: '',
    postalCode: '',
    city: '',
    country: '',
    vatId: '',
    billingEmail: '',
    invoiceContact: '',
    defaultCostCenter: '',
    leitwegId: '',
});

const makeInitialFormState = (): CreateOrgFormState => ({
    basic: makeInitialBasic(),
    customerClassId: null,
    billing: makeInitialBilling(),
    units: [],
});

const trimOrNull = (value: string): string | null => {
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
};

const OrganizationCreatePage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const tenant = useSelector(tenantSelector);

    const [form, setForm] = useState<CreateOrgFormState>(() => makeInitialFormState());
    const [error, setError] = useState<string | null>(null);
    // The save button is disabled while the form is invalid, so a submit can
    // never surface the field errors — they show LIVE instead. Format errors
    // (email/VAT/country) only fire on non-empty input, so a pristine form
    // stays clean; the required legal name waits until the field was touched.
    const [legalNameTouched, setLegalNameTouched] = useState(false);
    // A select cannot be "touched and still empty" the way a text field can, so
    // an unchosen type only becomes an error once the operator has started
    // filling in the basic information — on a pristine form it is simply not
    // answered yet, and a red field on an untouched form is noise.
    const [basicTouched, setBasicTouched] = useState(false);

    // ADR-0012: in `external` mode the tenant's own system owns the invoice, so
    // there is nothing for kolula to do with a VAT id or an invoice address.
    // Same resolution the Account and Services pages use — a bare
    // `useGetAccountQuery` would answer for the CALLER's tenant, not the one on
    // screen (#1334, #2059). On create there is never existing data to protect.
    const { accountTenant } = useViewedTenantAccount();
    const showBilling = shouldShowBillingSection(accountTenant?.invoiceIssuer, false);

    const [createOrganization, { isLoading: saving }] = useCreateOrganizationMutation();
    const [createOrganizationUnit] = useCreateOrganizationUnitMutation();
    // skip until the tenant is restored — hard-reload race with the tenant
    // cache reset middleware (see OrganizationsTab).
    const { data: customerClasses = [], isLoading: classesLoading } =
        useGetCustomerClassesQuery(undefined, { skip: !tenant });

    // Reset the form when the active tenant changes (CLAUDE.md: state tied to a
    // tenant must reset on context switch). Guard on the tenant id so the first
    // mount does not wipe the already-initialised form.
    const previousTenantRef = useRef(tenant);
    useEffect(() => {
        if (previousTenantRef.current !== tenant) {
            previousTenantRef.current = tenant;
            setForm(makeInitialFormState());
            setError(null);
            setLegalNameTouched(false);
            setBasicTouched(false);
        }
    }, [tenant]);

    const updateBasic = useCallback((updates: Partial<OrgBasicInfo>) => {
        if ('legalName' in updates) {
            setLegalNameTouched(true);
        }
        setBasicTouched(true);
        setForm((prev) => ({ ...prev, basic: { ...prev.basic, ...updates } }));
    }, []);

    const updateBilling = useCallback((updates: Partial<OrgBilling>) => {
        setForm((prev) => ({ ...prev, billing: { ...prev.billing, ...updates } }));
    }, []);

    const updateUnits = useCallback((units: OrgUnitFormRow[]) => {
        setForm((prev) => ({ ...prev, units }));
    }, []);

    const updateCustomerClass = useCallback((customerClassId: string | null) => {
        setForm((prev) => ({ ...prev, customerClassId }));
    }, []);

    // ─── Validation (derived from FORM STATE, not API) ──────────────────────
    const legalNameMissing = form.basic.legalName.trim() === '';
    const typeMissing = form.basic.type === '';
    const billingEmailInvalid = useMemo(
        () => form.billing.billingEmail.trim() !== '' && !isValidBillingEmail(form.billing.billingEmail),
        [form.billing.billingEmail],
    );
    const vatIdInvalid = useMemo(
        () => form.billing.vatId.trim() !== '' && !isValidVatId(form.billing.vatId),
        [form.billing.vatId],
    );
    const countryInvalid = useMemo(
        () => form.billing.country.trim() !== '' && !isValidCountryCode(form.billing.country),
        [form.billing.country],
    );

    const orgLeitwegIdProblem = useMemo(
        () => leitwegIdProblem(form.billing.leitwegId),
        [form.billing.leitwegId],
    );
    const leitwegIdInvalid = useMemo(
        () => orgLeitwegIdProblem !== null
            || form.units.some((unit) => leitwegIdProblem(unit.leitwegId) !== null),
        [orgLeitwegIdProblem, form.units],
    );

    const validationError = useMemo((): string | null => {
        if (legalNameMissing) return t('organization.validation.legal_name_required');
        if (typeMissing) return t('organization.validation.type_required');
        if (billingEmailInvalid) return t('organization.validation.billing_email_invalid');
        if (vatIdInvalid) return t('organization.validation.vat_id_invalid');
        if (countryInvalid) return t('organization.validation.country_invalid');
        if (leitwegIdInvalid) return t('organization.validation.leitweg_id_invalid');
        return null;
    }, [legalNameMissing, typeMissing, billingEmailInvalid, vatIdInvalid, countryInvalid, leitwegIdInvalid, t]);

    const buildBillingAddress = useCallback(() => {
        const street = form.billing.street.trim();
        const postalCode = form.billing.postalCode.trim();
        const city = form.billing.city.trim();
        const country = form.billing.country.trim();
        if (!street && !postalCode && !city && !country) return null;
        return { street, postalCode, city, country };
    }, [form.billing]);

    const handleSave = useCallback(async () => {
        setLegalNameTouched(true);
        setBasicTouched(true);
        if (validationError) {
            setError(validationError);
            return;
        }
        // Narrowing, not a cast of convenience: `validationError` above returns
        // early while the type is unchosen, so it is a real one here.
        const type = form.basic.type;
        if (type === '') return;
        setError(null);
        try {
            const created = await createOrganization({
                body: {
                    legalName: form.basic.legalName.trim(),
                    displayName: trimOrNull(form.basic.displayName),
                    type,
                    customerClassId: form.customerClassId,
                    billingAddress: buildBillingAddress(),
                    vatId: trimOrNull(form.billing.vatId),
                    billingEmail: trimOrNull(form.billing.billingEmail),
                    invoiceContact: trimOrNull(form.billing.invoiceContact),
                    defaultCostCenter: trimOrNull(form.billing.defaultCostCenter),
                    leitwegId: trimOrNull(form.billing.leitwegId),
                    status: form.basic.status,
                    // #2163 — interpreted, not stored: the backend designates
                    // an existing account or writes an owner-invite. Sent only
                    // when filled, so a create that ignores it behaves exactly
                    // as before.
                    ...(form.basic.contactEmail.trim()
                        ? { contactEmail: form.basic.contactEmail.trim() }
                        : {}),
                    notes: trimOrNull(form.basic.notes),
                },
            }).unwrap();
            // Units are a separate resource (plan §5): persist each staged,
            // non-empty unit against the freshly created org before navigating.
            const namedUnits = form.units.filter((u) => u.name.trim() !== '');
            for (const unit of namedUnits) {
                await createOrganizationUnit({
                    organizationId: created.id,
                    body: {
                        name: unit.name.trim(),
                        customerClassId: unit.customerClassId,
                        defaultCostCenter: trimOrNull(unit.defaultCostCenter),
                        leitwegId: trimOrNull(unit.leitwegId),
                    },
                }).unwrap();
            }
            navigate(makeOrganizationDetailUrl(created.id));
        } catch (err: unknown) {
            const e = err as { data?: { message?: string }, message?: string };
            setError(e?.data?.message ?? e?.message ?? t('organization.create.save_error'));
        }
    }, [validationError, form, buildBillingAddress, createOrganization, createOrganizationUnit, navigate, t]);

    return (
        <div className={'p-6 flex flex-col gap-6'}>
            <div className={'sticky top-16 z-30 bg-background -mx-6 px-6 -mt-6 pt-6 pb-3'}>
                <PageHeader>
                    <PageHeadline>
                        {t('organization.create.title')}
                        {form.basic.legalName.trim() && (
                            <span className={'block text-sm font-normal text-foreground/60 mt-0.5'}>
                                {form.basic.legalName.trim()}
                            </span>
                        )}
                    </PageHeadline>
                </PageHeader>
            </div>

            {error && (
                <div className={'bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-800'}>
                    {error}
                </div>
            )}

            {/* Says once, up front, what the asterisks mean. Without it the
                only signal that a field is required is a `*` whose convention
                the reader has to already know — and previously the first
                mention of it was an error message after a failed save. */}
            <p className={'text-sm text-foreground/60'}>{t('common.required_fields')}</p>

            <OrgBasicInfoSection
                value={form.basic}
                onChange={updateBasic}
                legalNameError={legalNameTouched && legalNameMissing}
                typeError={basicTouched && typeMissing}
            />

            <OrgClassSection
                value={form.customerClassId}
                onChange={updateCustomerClass}
                classes={customerClasses}
                isLoading={classesLoading}
            />

            {showBilling && (
                <OrgBillingSection
                    value={form.billing}
                    onChange={updateBilling}
                    billingEmailError={billingEmailInvalid}
                    vatIdError={vatIdInvalid}
                    countryError={countryInvalid}
                    leitwegIdError={orgLeitwegIdProblem}
                />
            )}

            <OrgUnitsSection
                units={form.units}
                onChange={updateUnits}
                classes={customerClasses}
                isLoading={classesLoading}
            />

            <div className={'sticky bottom-0 bg-text-contrast border-t border-foreground/10 px-6 py-3 '
                + 'flex items-center justify-end gap-3 shadow-lg z-10 -mx-6 -mb-6'}
            >
                {/* The save button is disabled while the form is invalid, so the
                    reason is stated inline — a hover tooltip on a disabled
                    button is unreliable and hides why the button is dead. */}
                {validationError && (
                    <p className={'mr-auto text-sm text-foreground/60'}>{validationError}</p>
                )}
                <CustomButton
                    title={t('common.cancel')}
                    onClick={() => navigate(paths.customers)}
                    isFilled={false}
                    disabled={saving}
                />
                <CustomButton
                    title={t('organization.create.save')}
                    onClick={handleSave}
                    isFilled={true}
                    disabled={saving || validationError !== null}
                    loading={saving}
                />
            </div>
        </div>
    );
};

export default OrganizationCreatePage;
